import { useState } from 'react';
import { ApiClient } from '../../api/client';
import { useTranslation } from '../../i18n';
import { UserConfig } from '../../types';

interface TelegramFileTypeSettingsProps {
  userConfig: UserConfig | null;
  onConfigUpdate: (newConfig: UserConfig) => void;
}

export function TelegramFileTypeSettings({ userConfig, onConfigUpdate }: TelegramFileTypeSettingsProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { t } = useTranslation();

  const currentFileType = userConfig?.telegram_file_type || 'document';

  const handleFileTypeChange = async (newType: 'document' | 'video') => {
    if (newType === currentFileType || isUpdating) return;

    setIsUpdating(true);
    setMessage(null);

    try {
      await ApiClient.updateTelegramFileType(newType);
      
      // Update the user config
      if (userConfig) {
        const updatedConfig = { ...userConfig, telegram_file_type: newType };
        onConfigUpdate(updatedConfig);
      }

      setMessage({
        type: 'success',
        text: t('profile.settings.telegramFileType.updateSuccess')
      });

      // Clear success message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update telegram file type:', error);
      setMessage({
        type: 'error',
        text: t('profile.settings.telegramFileType.updateError')
      });

      // Clear error message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-theme-text mb-2">
          {t('profile.settings.telegramFileType.title')}
        </h3>
        <p className="text-theme-hint text-sm mb-4">
          {t('profile.settings.telegramFileType.description')}
        </p>

                <div className="space-y-3">
          {/* Document Option */}
          <div 
            className={`relative p-4 rounded-lg cursor-pointer transition-all duration-200 ${
              currentFileType === 'document'
                ? 'bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-500'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            } ${isUpdating ? 'pointer-events-none opacity-70' : ''}`}
            onClick={() => handleFileTypeChange('document')}
          >
            <div className="flex items-center gap-3">
              {/* Radio Button */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                currentFileType === 'document'
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300 dark:border-gray-600 bg-transparent'
              }`}>
                {currentFileType === 'document' && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${
                      currentFileType === 'document' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {t('profile.settings.telegramFileType.document')}
                    </h4>
                    <p className={`text-sm ${
                      currentFileType === 'document' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {t('profile.settings.telegramFileType.documentDescription')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {currentFileType === 'document' && (
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded">
                        {t('profile.settings.telegramFileType.selected')}
                      </span>
                    )}
                    <span className={`material-symbols-outlined ${
                      currentFileType === 'document' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                    }`}>
                      description
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video Option */}
          <div 
            className={`relative p-4 rounded-lg cursor-pointer transition-all duration-200 ${
              currentFileType === 'video'
                ? 'bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-500'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            } ${isUpdating ? 'pointer-events-none opacity-70' : ''}`}
            onClick={() => handleFileTypeChange('video')}
          >
            <div className="flex items-center gap-3">
              {/* Radio Button */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                currentFileType === 'video'
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300 dark:border-gray-600 bg-transparent'
              }`}>
                {currentFileType === 'video' && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${
                      currentFileType === 'video' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {t('profile.settings.telegramFileType.video')}
                    </h4>
                    <p className={`text-sm ${
                      currentFileType === 'video' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {t('profile.settings.telegramFileType.videoDescription')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {currentFileType === 'video' && (
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded">
                        {t('profile.settings.telegramFileType.selected')}
                      </span>
                    )}
                    <span className={`material-symbols-outlined ${
                      currentFileType === 'video' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                    }`}>
                      play_circle
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status/Loading indicator */}
        {isUpdating && (
          <div className="mt-4 flex items-center gap-2 text-theme-hint justify-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="w-4 h-4 border-2 border-theme-hint border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm font-medium">{t('profile.settings.telegramFileType.updating')}</span>
          </div>
        )}

        {/* Success/Error message */}
        {message && (
          <div className={`mt-4 p-4 rounded-lg text-sm font-medium flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            <span className="material-symbols-outlined">
              {message.type === 'success' ? 'check_circle' : 'error'}
            </span>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
} 